import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { RainbowEAKC } from "../shared/RainbowEAKC";

function glow(color: string, strong = false): string {
  return strong
    ? `0 0 20px ${color}99, 0 0 50px ${color}50, 0 0 100px ${color}20`
    : `0 0 12px ${color}60, 0 0 30px ${color}25`;
}

const STATS = [
  { value: "6", label: "MODES",  color: COLORS.neonGreen },
  { value: "5", label: "LAYERS", color: COLORS.neonBlue },
  { value: "∞", label: "TOOLS",  color: COLORS.neonOrange },
  { value: "0", label: "CODE",   color: COLORS.neonPink },
];

const ICONS = ["🧠", "📄", "⚡", "🔧", "📊", "🤖"];
const ICON_COLORS = [
  COLORS.neonPink,
  COLORS.neonBlue,
  COLORS.neonYellow,
  COLORS.neonGreen,
  COLORS.neonOrange,
  COLORS.neonCyan,
];

const PHASES = [
  { label: "Phase 1", done: true,  animFill: false, color: COLORS.neonGreen },
  { label: "Phase 2", done: false, animFill: true,  color: COLORS.neonBlue },
  { label: "Phase 3", done: false, animFill: false, color: COLORS.textMuted },
  { label: "Phase 4", done: false, animFill: false, color: COLORS.textMuted },
  { label: "Phase 5", done: false, animFill: false, color: COLORS.textMuted },
];

/* Drifting background orb */
const DriftOrb: React.FC<{
  cx: number;
  cy: number;
  r: number;
  color: string;
  frame: number;
  speed: number;
  phase: number;
}> = ({ cx, cy, r, color, frame, speed, phase }) => {
  const dx = Math.sin(frame * speed + phase) * 40;
  const dy = Math.cos(frame * speed * 0.7 + phase) * 30;
  return (
    <div
      style={{
        position: "absolute",
        left: cx + dx - r,
        top: cy + dy - r,
        width: r * 2,
        height: r * 2,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${color}0a 0%, transparent 70%)`,
        pointerEvents: "none",
      }}
    />
  );
};

/* Small floating particle */
const Particle: React.FC<{
  x: number;
  y: number;
  size: number;
  color: string;
  frame: number;
  freq: number;
  phase: number;
  layer: number;
}> = ({ x, y, size, color, frame, freq, phase, layer }) => {
  const px = x + Math.sin(frame * freq + phase) * 18 * layer;
  const py = y + Math.cos(frame * freq * 0.8 + phase) * 14 * layer;
  return (
    <div
      style={{
        position: "absolute",
        left: px,
        top: py,
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        boxShadow: `0 0 ${size * 3}px ${color}`,
        opacity: 0.55,
        pointerEvents: "none",
      }}
    />
  );
};

export const Scene12Outro: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  /* Background grid drift */
  const gridShift = Math.sin(frame * 0.006) * 8;

  /* Title spring — bounce in at frame 0 */
  const titleSp = spring({ frame, fps, config: SPRING.bouncy });
  const titleScale = interpolate(titleSp, [0, 1], [0.6, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const titleOp = interpolate(titleSp, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  /* Subtitle spring — frame 15 */
  const subSp = spring({ frame: frame - 15, fps, config: SPRING.gentle });
  const subOp = interpolate(subSp, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  /* 3D icon ring rotation — 0.5 deg/frame */
  const ringAngleDeg = frame * 0.5;

  /* Icon ring breathing — subtle rotateX */
  const ringBreath = Math.sin(frame * 0.04) * 1; // ±1 deg

  /* Phase bar spring — frame 80 */
  const phaseSp = spring({ frame: frame - 80, fps, config: SPRING.snappy });
  const phaseOp = interpolate(phaseSp, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  /* Phase 2 animated fill */
  const phase2Fill = interpolate(
    spring({ frame: frame - 90, fps, config: SPRING.slow }),
    [0, 1],
    [0, 100],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  /* Footer spring — frame 120 */
  const footerSp = spring({ frame: frame - 120, fps, config: SPRING.gentle });
  const footerOp = interpolate(footerSp, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  /* URL glow pulse */
  const urlGlowPx = interpolate(Math.sin(frame * 0.08), [-1, 1], [12, 36], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  /* Rainbow gradient angle — shifts with frame */

  return (
    <AbsoluteFill style={{ background: COLORS.bg, overflow: "hidden" }}>
      {/* ── Background orbs (parallax 0.3x — slow drift) ── */}
      <DriftOrb cx={200}  cy={200}  r={300} color={COLORS.neonPurple} frame={frame} speed={0.008} phase={0} />
      <DriftOrb cx={1700} cy={300}  r={280} color={COLORS.neonBlue}   frame={frame} speed={0.006} phase={2} />
      <DriftOrb cx={960}  cy={800}  r={350} color={COLORS.neonPink}   frame={frame} speed={0.007} phase={4} />
      <DriftOrb cx={400}  cy={700}  r={200} color={COLORS.neonGreen}  frame={frame} speed={0.010} phase={1} />
      <DriftOrb cx={1500} cy={750}  r={220} color={COLORS.neonOrange} frame={frame} speed={0.009} phase={3} />

      {/* ── Grid lines ── */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `linear-gradient(${COLORS.neonPurple}0d 1px, transparent 1px), linear-gradient(90deg, ${COLORS.neonPurple}0d 1px, transparent 1px)`,
          backgroundSize: "80px 80px",
          transform: `translateY(${gridShift}px)`,
        }}
      />

      {/* ── Floating particles (8 at different depths) ── */}
      <Particle x={150}  y={400} size={5} color={COLORS.neonPink}   frame={frame} freq={0.020} phase={0}   layer={0.6} />
      <Particle x={350}  y={550} size={4} color={COLORS.neonGreen}  frame={frame} freq={0.015} phase={1.2} layer={0.8} />
      <Particle x={700}  y={200} size={6} color={COLORS.neonBlue}   frame={frame} freq={0.018} phase={2.4} layer={0.5} />
      <Particle x={1100} y={180} size={4} color={COLORS.neonYellow} frame={frame} freq={0.022} phase={0.8} layer={0.7} />
      <Particle x={1400} y={500} size={5} color={COLORS.neonCyan}   frame={frame} freq={0.013} phase={3.1} layer={0.9} />
      <Particle x={1650} y={650} size={4} color={COLORS.neonOrange} frame={frame} freq={0.017} phase={1.7} layer={0.6} />
      <Particle x={800}  y={900} size={6} color={COLORS.neonPurple} frame={frame} freq={0.019} phase={2.9} layer={0.8} />
      <Particle x={1200} y={850} size={3} color={COLORS.neonPink}   frame={frame} freq={0.016} phase={0.5} layer={0.5} />

      {/* ── Stats row (top, y≈60-140) ── */}
      <div
        style={{
          position: "absolute",
          top: 60,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 80,
        }}
      >
        {STATS.map((s, i) => {
          const sp = spring({
            frame: frame - (30 + i * 12),
            fps,
            config: SPRING.punch,
          });
          const op = interpolate(sp, [0, 1], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
          const sc = interpolate(sp, [0, 1], [0.3, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
          const breath = 1 + Math.sin(frame * 0.03 + i) * 0.02;

          return (
            <div
              key={s.label}
              style={{
                opacity: op,
                transform: `scale(${sc * breath})`,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: 52,
                  fontFamily: FONTS.heading,
                  fontWeight: 900,
                  color: s.color,
                  textShadow: glow(s.color, true),
                  lineHeight: 1,
                }}
              >
                {s.value}
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontFamily: FONTS.mono,
                  color: COLORS.textMuted,
                  marginTop: 8,
                  textTransform: "uppercase" as const,
                  letterSpacing: 3,
                }}
              >
                {s.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Center section: 3D icon ring + EAKC title ── */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          perspective: 1200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Icon ring container — breathing rotateX */}
        <div
          style={{
            position: "relative",
            width: 0,
            height: 0,
            transform: `rotateX(${ringBreath}deg)`,
          }}
        >
          {ICONS.map((icon, i) => {
            const angleDeg = ringAngleDeg + i * (360 / ICONS.length);
            const angleRad = (angleDeg * Math.PI) / 180;
            const rx = 260; // horizontal radius
            const ry = 100; // vertical radius (elliptical)
            const ix = Math.cos(angleRad) * rx;
            const iy = Math.sin(angleRad) * ry;
            const sinVal = Math.sin(angleRad);
            const isInFront = sinVal > 0;
            const depthScale = isInFront ? 1 : 0.7;
            const zIdx = isInFront ? 10 : 1;
            const iconColor = ICON_COLORS[i];

            const iconSp = spring({
              frame: frame - i * 5,
              fps,
              config: SPRING.bouncy,
            });
            const iconSc = interpolate(iconSp, [0, 1], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });

            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: ix - 26,
                  top: iy - 26,
                  width: 52,
                  height: 52,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 26,
                  background: `${iconColor}20`,
                  borderRadius: "50%",
                  border: `1.5px solid ${iconColor}55`,
                  transform: `scale(${iconSc * depthScale})`,
                  filter: `drop-shadow(0 0 12px ${iconColor}cc)`,
                  zIndex: zIdx,
                }}
              >
                {icon}
              </div>
            );
          })}
        </div>

        {/* EAKC title + subtitle — centered in the ring */}
        <div
          style={{
            position: "absolute",
            textAlign: "center",
            transform: `scale(${titleScale})`,
            opacity: titleOp,
            zIndex: 5,
          }}
        >
          <RainbowEAKC
            id="vr-outro"
            frame={frame}
            fontSize={140}
            fontFamily={FONTS.heading}
            glowColor={COLORS.neonPurple}
          />

          {/* Subtitle */}
          <div
            style={{
              fontSize: 36,
              fontFamily: FONTS.heading,
              fontWeight: 600,
              color: COLORS.neonCyan,
              textShadow: glow(COLORS.neonCyan, true),
              marginTop: 12,
              opacity: subOp,
              letterSpacing: 1,
            }}
          >
            Your Company's Brain
          </div>
        </div>
      </div>

      {/* ── Phase progress bar (bottom area, y≈680) ── */}
      <div
        style={{
          position: "absolute",
          bottom: 180,
          left: "50%",
          transform: "translateX(-50%)",
          opacity: phaseOp,
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
        }}
      >
        {PHASES.map((p, i) => {
          const fillPct = p.done
            ? 100
            : p.animFill
            ? phase2Fill
            : 0;

          const barColor = p.done || p.animFill ? p.color : COLORS.textMuted;

          return (
            <div
              key={p.label}
              style={{
                display: "flex",
                flexDirection: "column" as const,
                alignItems: "center",
                gap: 6,
              }}
            >
              {/* Bar track */}
              <div
                style={{
                  width: 160,
                  height: 10,
                  borderRadius: 5,
                  background: `${barColor}25`,
                  border: `1px solid ${barColor}40`,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${fillPct}%`,
                    background: p.color,
                    boxShadow:
                      p.done || p.animFill ? glow(p.color) : "none",
                    borderRadius: 5,
                  }}
                />
              </div>

              {/* Label */}
              <div
                style={{
                  fontSize: 11,
                  fontFamily: FONTS.mono,
                  color: p.done || p.animFill ? p.color : COLORS.textMuted,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  textShadow:
                    p.done || p.animFill ? glow(p.color) : "none",
                }}
              >
                {p.label}
                {p.done && <span style={{ fontSize: 10 }}>✅</span>}
                {p.animFill && (
                  <span style={{ fontSize: 10, color: COLORS.neonBlue }}>
                    🔵
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Footer (y≈800-900) ── */}
      <div
        style={{
          position: "absolute",
          bottom: 48,
          left: "50%",
          transform: "translateX(-50%)",
          opacity: footerOp,
          textAlign: "center",
          whiteSpace: "nowrap" as const,
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontFamily: FONTS.mono,
            fontWeight: 600,
            color: COLORS.neonCyan,
            textShadow: `0 0 ${urlGlowPx}px ${COLORS.neonCyan}80, 0 0 ${urlGlowPx * 0.5}px ${COLORS.neonCyan}40`,
            letterSpacing: 1,
            marginBottom: 8,
          }}
        >
          eakc.adaptivesmartsystems.cc
        </div>
        <div
          style={{
            fontSize: 16,
            fontFamily: FONTS.body,
            color: COLORS.textMuted,
            marginBottom: 4,
          }}
        >
          Built by Prabhat Tiwari
        </div>
        <div
          style={{
            fontSize: 13,
            fontFamily: FONTS.body,
            color: COLORS.textMuted,
            opacity: 0.7,
          }}
        >
          April 2026
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
